import {
  Tokens,
  AuthAdapter,
  SignInWithPasswordResult,
  SignInWithPasswordSuccess,
} from '../../types';
import ApigeeAuthAdapter from '../ApigeeAuthAdapter';
import AmplifyAuthAdapter from '../AmplifyAuthAdapter';

class HybridAuthAdapter implements AuthAdapter {
  /**
   * Authenticates user with password and stores tokens in storage
   */
  constructor(
    private amplifyAdapter: AmplifyAuthAdapter,
    private apigeeAdapter: ApigeeAuthAdapter, // eslint-disable-next-line no-empty-function
  ) {}

  async signInWithPassword(
    email: string,
    password: string,
  ): Promise<SignInWithPasswordResult> {
    const amplifyResult = await this.amplifyAdapter.signInWithPassword(
      email,
      password,
    );
    if (
      amplifyResult.status === 'MFA_REQUIRED' ||
      amplifyResult.status !== 'SUCCESS'
    ) {
      return amplifyResult;
    }

    const amplifyTokens = amplifyResult.tokens;
    const apigeeTokens = await this.apigeeAdapter.signInWithCustomToken(
      amplifyTokens.idToken?.token!,
      email,
    );

    return this.signInSuccess(this.buildToken({ amplifyTokens, apigeeTokens }));
  }

  /**
   * Authenticates a user with a custom token and stores token in
   * storage
   */
  async signInWithCustomToken(
    token: string,
    username: string,
  ): Promise<Tokens> {
    const amplifyTokens = await this.amplifyAdapter.signInWithCustomToken(
      token,
      username,
    );
    const apigeeTokens = await this.apigeeAdapter.signInWithCustomToken(
      amplifyTokens.idToken?.token!,
      username,
    );

    return this.buildToken({ amplifyTokens, apigeeTokens });
  }

  async refreshTokens(idToken: string) {
    const amplifyTokens = await this.amplifyAdapter.refreshTokens(idToken);
    const freshIdToken = amplifyTokens.idToken?.token || idToken;
    const apigeeTokens = await this.apigeeAdapter.refreshTokens(freshIdToken);
    return this.buildToken({ amplifyTokens, apigeeTokens });
  }

  async signInWithMultiFactorAuth(verificationCode: string) {
    const amplifyTokens = await this.amplifyAdapter.signInWithMultiFactorAuth(
      verificationCode,
    );
    const apigeeTokens = await this.apigeeAdapter.signInWithCustomToken(
      amplifyTokens.idToken?.token!,
      '',
    );
    return this.buildToken({ amplifyTokens, apigeeTokens });
  }

  /**
   * Builds a hybrid apigee/amplify token
   */
  buildToken(response: {
    amplifyTokens: Tokens;
    apigeeTokens: Tokens;
  }): Tokens {
    return {
      accessToken: response.apigeeTokens.accessToken,
      idToken: response.amplifyTokens.idToken,
      refreshToken: response.amplifyTokens.refreshToken,
      expiresAt: response.apigeeTokens.expiresAt,
    };
  }

  // Currently only used by invoice and/or installer portals. Not used by the main CS secure flow.
  async changePassword(
    email: string,
    oldPassword: string,
    newPassword: string,
  ): Promise<SignInWithPasswordResult> {
    const amplifyResult = await this.amplifyAdapter.changePassword(
      email,
      oldPassword,
      newPassword,
    );
    if (amplifyResult.status !== 'SUCCESS') {
      return amplifyResult;
    }

    const amplifyTokens = amplifyResult.tokens;

    const apigeeTokens = await this.apigeeAdapter.signInWithCustomToken(
      amplifyTokens.idToken?.token!,
      email,
    );

    return this.signInSuccess(this.buildToken({ amplifyTokens, apigeeTokens }));
  }

  // Currently only used by invoice and/or installer portals. Not used by the main CS secure flow.
  async forgotPassword(username: string): Promise<Boolean> {
    const result = await this.amplifyAdapter.forgotPassword(username);
    return result;
  }

  // Currently only used by invoice and/or installer portals. Not used by the main CS secure flow.
  async verifyAndResetPassword(
    username: string,
    code: string,
    newPassword: string,
  ): Promise<Boolean> {
    const result = await this.amplifyAdapter.verifyAndResetPassword(
      username,
      code,
      newPassword,
    );
    return result;
  }

  private signInSuccess(tokens: Tokens): SignInWithPasswordSuccess {
    return {
      status: 'SUCCESS',
      tokens,
    };
  }
}

export default HybridAuthAdapter;
